import React, { useEffect, useMemo, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import "./app-layout.scss";
import logo from "../../assets/images/logo.png";

const menuItems = [
  { label: "Dashboard", icon: "▦", path: "/", end: true },
  { label: "Leads", icon: "▤", path: "/news" },
  { label: "AI Management", icon: "✺", path: "/ai-management" },
  { label: "Search Management", icon: "⚙", path: "/searchmanagement" },
  { label: "Reports", icon: "▥", path: "/reports" },
  { label: "Audit Logs", icon: "◷", path: "/audit-logs" },
  { label: "Administrative", icon: "⚇", path: "/administrative" },
  { label: "Settings", icon: "⚙", path: "/settings" },
];

const AppLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);

  useEffect(() => {
    const saved = window.localStorage.getItem("yk_sidebar_collapsed");
    if (saved === "1") {
      setSidebarCollapsed(true);
    }
  }, []);

  const handleToggleSidebar = () => {
    setSidebarCollapsed((current) => {
      const next = !current;
      window.localStorage.setItem("yk_sidebar_collapsed", next ? "1" : "0");
      return next;
    });
  };

  const pageTitle = useMemo(() => {
    const matched = menuItems.find((item) => {
      if (item.end) return location.pathname === item.path;
      return location.pathname.startsWith(item.path);
    });

    return matched?.label || "Dashboard";
  }, [location.pathname]);

  const handleEditProfile = () => {
    setProfileMenuOpen(false);
    navigate("/dashboard/settings");
  };

  const handleLogout = () => {
    setProfileMenuOpen(false);
    localStorage.removeItem("token");
    navigate("/login");
  };

  return (
    <div className={`app-layout ${sidebarCollapsed ? "app-layout--sidebar-collapsed" : ""}`}>
      <aside
        className={`app-layout__sidebar ${sidebarOpen ? "app-layout__sidebar--open" : ""} ${sidebarCollapsed ? "app-layout__sidebar--collapsed" : ""}`}
      >
        <div className="app-layout__brand">
          <img className="app-layout__logo" src={logo} alt="YKMW Group of Companies" />
        </div>

        <nav className="app-layout__menu">
          {menuItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.end}
              className={({ isActive }) =>
                `app-layout__menu-item ${isActive ? "app-layout__menu-item--active" : ""}`
              }
              onClick={() => setSidebarOpen(false)}
            >
              <span className="app-layout__menu-icon">{item.icon}</span>
              <span className="app-layout__menu-label">{item.label}</span>
            </NavLink>
          ))}
        </nav>
      </aside>

      {sidebarOpen && (
        <button
          className="app-layout__overlay"
          type="button"
          aria-label="Close menu"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <main className="app-layout__main">
        <header className="app-layout__topbar">
          <div className="app-layout__topbar-left">
            <button
              className="app-layout__menu-toggle"
              type="button"
              aria-label="Open mobile menu"
              onClick={() => setSidebarOpen(true)}
            >
              ☰
            </button>

            <button
              className="app-layout__collapse-toggle"
              type="button"
              aria-label={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
              onClick={handleToggleSidebar}
            >
              ☰
            </button>

            <h1 className="app-layout__title">{pageTitle}</h1>
          </div>

          <div className="app-layout__topbar-actions">
            <div className="app-layout__profile-wrap">
              <button
                className={`app-layout__profile ${profileMenuOpen ? "app-layout__profile--open" : ""}`}
                type="button"
                aria-haspopup="menu"
                aria-expanded={profileMenuOpen}
                onClick={() => setProfileMenuOpen((open) => !open)}
              >
                <span className="app-layout__avatar">A</span>
                <span className="app-layout__profile-text">
                  <strong>Admin User</strong>
                  <small>Administrator</small>
                </span>
                <span className="app-layout__chevron">⌄</span>
              </button>

              {profileMenuOpen && (
                <>
                  <button
                    className="app-layout__profile-backdrop"
                    type="button"
                    aria-label="Close profile menu"
                    onClick={() => setProfileMenuOpen(false)}
                  />
                  <div className="app-layout__profile-menu" role="menu">
                    <button type="button" role="menuitem" onClick={handleEditProfile}>
                      <span>👤</span>
                      Edit Profile
                    </button>
                    <button type="button" role="menuitem" onClick={handleLogout}>
                      <span>↪</span>
                      Logout
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </header>

        <section className="app-layout__content">
          <Outlet />
        </section>
      </main>
    </div>
  );
};

export default AppLayout;
